/**
 * Canonical userdata helpers for annotation features and Paper.js items.
 *
 * Canonical location (toolkit GeoJSON): feature.properties.userdata.girderProjects.*
 * Runtime location (Paper.js): item.data.userdata (hydrated from feature.properties.userdata by osd-paperjs-annotation).
 */

const GIRDER_PROJECTS_NS = 'girderProjects';

function isObject(v) {
    return v && typeof v === 'object' && !Array.isArray(v);
}

function deepMerge(base, patch) {
    if (!isObject(base)) return patch;
    if (!isObject(patch)) return base;
    const out = { ...base };
    Object.keys(patch).forEach((k) => {
        const bv = base[k];
        const pv = patch[k];
        if (isObject(bv) && isObject(pv)) out[k] = deepMerge(bv, pv);
        else out[k] = pv;
    });
    return out;
}

export function getFeatureUserdata(feature) {
    const ud = feature?.properties?.userdata;
    return isObject(ud) ? ud : {};
}

export function setFeatureUserdata(feature, patch) {
    const prev = getFeatureUserdata(feature);
    const next = deepMerge(prev, patch);
    return {
        ...(feature || {}),
        properties: {
            ...((feature && feature.properties) || {}),
            userdata: next,
        },
    };
}

export function getFeatureGirderProjectsUserdata(feature) {
    const ud = getFeatureUserdata(feature);
    return isObject(ud[GIRDER_PROJECTS_NS]) ? ud[GIRDER_PROJECTS_NS] : {};
}

export function setFeatureGirderProjectsUserdata(feature, patch) {
    return setFeatureUserdata(feature, { [GIRDER_PROJECTS_NS]: patch });
}

export function getPaperItemUserdata(item) {
    const ud = item?.data?.userdata;
    return isObject(ud) ? ud : {};
}

export function setPaperItemUserdata(item, patch) {
    if (!item) return;
    if (!item.data) item.data = {};
    const prev = getPaperItemUserdata(item);
    item.data.userdata = deepMerge(prev, patch);
}

export function getPaperItemGirderProjectsUserdata(item) {
    const ud = getPaperItemUserdata(item);
    return isObject(ud[GIRDER_PROJECTS_NS]) ? ud[GIRDER_PROJECTS_NS] : {};
}

export function setPaperItemGirderProjectsUserdata(item, patch) {
    setPaperItemUserdata(item, { [GIRDER_PROJECTS_NS]: patch });
}

export function getBoundingBoxCategoryFromUserdata(ud) {
    const gp = isObject(ud?.[GIRDER_PROJECTS_NS]) ? ud[GIRDER_PROJECTS_NS] : null;
    const bb = isObject(gp?.boundingBox) ? gp.boundingBox : null;
    return {
        categoryId: bb?.categoryId ?? null,
        categoryLabel: bb?.categoryLabel ?? null,
    };
}

export function getBoundingBoxCategoryFromFeature(feature) {
    return getBoundingBoxCategoryFromUserdata(getFeatureUserdata(feature));
}

export function getBoundingBoxCategoryFromPaperItem(item) {
    return getBoundingBoxCategoryFromUserdata(getPaperItemUserdata(item));
}

export function setBoundingBoxCategoryOnFeature(feature, { categoryId, categoryLabel }) {
    return setFeatureGirderProjectsUserdata(feature, {
        boundingBox: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

export function setBoundingBoxCategoryOnPaperItem(item, { categoryId, categoryLabel }) {
    setPaperItemGirderProjectsUserdata(item, {
        boundingBox: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

export function getSegmentationCategoryFromUserdata(ud) {
    const gp = isObject(ud?.[GIRDER_PROJECTS_NS]) ? ud[GIRDER_PROJECTS_NS] : null;
    const seg = isObject(gp?.segmentation) ? gp.segmentation : null;
    return {
        categoryId: seg?.categoryId ?? null,
        categoryLabel: seg?.categoryLabel ?? null,
    };
}

export function getSegmentationCategoryFromFeature(feature) {
    return getSegmentationCategoryFromUserdata(getFeatureUserdata(feature));
}

export function getSegmentationCategoryFromPaperItem(item) {
    return getSegmentationCategoryFromUserdata(getPaperItemUserdata(item));
}

export function setSegmentationCategoryOnFeature(feature, { categoryId, categoryLabel }) {
    return setFeatureGirderProjectsUserdata(feature, {
        segmentation: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

export function setSegmentationCategoryOnPaperItem(item, { categoryId, categoryLabel }) {
    setPaperItemGirderProjectsUserdata(item, {
        segmentation: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

export function getPointCategoryFromUserdata(ud) {
    const gp = isObject(ud?.[GIRDER_PROJECTS_NS]) ? ud[GIRDER_PROJECTS_NS] : null;
    const pt = isObject(gp?.point) ? gp.point : null;
    return {
        categoryId: pt?.categoryId ?? null,
        categoryLabel: pt?.categoryLabel ?? null,
    };
}

export function getPointCategoryFromFeature(feature) {
    return getPointCategoryFromUserdata(getFeatureUserdata(feature));
}

export function getPointCategoryFromPaperItem(item) {
    return getPointCategoryFromUserdata(getPaperItemUserdata(item));
}

export function setPointCategoryOnFeature(feature, { categoryId, categoryLabel }) {
    return setFeatureGirderProjectsUserdata(feature, {
        point: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

export function setPointCategoryOnPaperItem(item, { categoryId, categoryLabel }) {
    setPaperItemGirderProjectsUserdata(item, {
        point: {
            ...(categoryId != null && { categoryId }),
            ...(categoryLabel != null && { categoryLabel }),
        },
    });
}

